import { lazy, Suspense, useEffect } from 'react';
import type { ComponentType } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { MeResponse } from '@yrp/shared/auth';
import { TeamsWorkspaceView } from '@yrp/shared/workspace-state';
import { isAgentLikeRole, type User } from '@yrp/shared/user';
import { apiFetch } from './lib/api.js';
import { queryClient } from './lib/queryClient.js';
import { AppLayout } from './components/layout/Shell.js';
import { activitySearch, contactActivitySearch } from './features/website-activity/rules.js';

function lazyPage<TModule, TKey extends keyof TModule>(
  load: () => Promise<TModule>,
  key: TKey,
) {
  return lazy(() => load().then((module) => ({ default: module[key] as ComponentType })));
}

// Lazy-loaded pages — initial bundle only contains the router + root layout.
const LoginPage = lazyPage(() => import('./features/auth/LoginPage.js'), 'LoginPage');
const DashboardPage = lazyPage(() => import('./features/dashboard/DashboardPage.js'), 'DashboardPage');
const ContactListPage = lazyPage(() => import('./features/crm/ContactListPage.js'), 'ContactListPage');
const VeteransUnitedPage = lazyPage(() => import('./features/veterans-united/VeteransUnitedPage.js'), 'VeteransUnitedPage');
const VuLeadDetailPage = lazyPage(() => import('./features/veterans-united/VuLeadDetailPage.js'), 'VuLeadDetailPage');
const ContactDetailPage = lazyPage(() => import('./features/crm/ContactDetailPage.js'), 'ContactDetailPage');
const WebsiteActivityPage = lazyPage(() => import('./features/website-activity/WebsiteActivityPage.js'), 'WebsiteActivityPage');
const SettingsPage = lazyPage(() => import('./features/settings/SettingsPage.js'), 'SettingsPage');
const ReviewsPage = lazyPage(() => import('./features/reviews/ReviewsPage.js'), 'ReviewsPage');
const PublicReviewPage = lazyPage(() => import('./features/reviews/PublicReviewPage.js'), 'PublicReviewPage');
const PrivacyPolicyPage = lazyPage(() => import('./features/legal/PublicLegalPages.js'), 'PrivacyPolicyPage');
const TermsOfServicePage = lazyPage(() => import('./features/legal/PublicLegalPages.js'), 'TermsOfServicePage');
const CockpitPreviewPage = lazyPage(() => import('./features/voice/cockpit/CockpitPreviewPage.js'), 'CockpitPreviewPage');
const MessagingPreviewPage = lazyPage(() => import('./features/messaging/cockpit/MessagingPreviewPage.js'), 'MessagingPreviewPage');
const DedupePage = lazyPage(() => import('./features/dedupe/DedupePage.js'), 'DedupePage');
const MailPage = lazyPage(() => import('./features/mail/MailPage.js'), 'MailPage');
const FilesPage = lazyPage(() => import('./features/files/FilesPage.js'), 'FilesPage');
const CommsPage = lazyPage(() => import('./features/comms/CommsPage.js'), 'CommsPage');
const CommsAnalyticsPage = lazyPage(() => import('./features/comms/CommsAnalyticsPage.js'), 'CommsAnalyticsPage');
const GroupsPage = lazyPage(() => import('./features/comms/GroupsPage.js'), 'GroupsPage');
const MessengerPage = lazyPage(() => import('./features/messenger/MessengerPage.js'), 'MessengerPage');
const TransactionListPage = lazyPage(() => import('./features/transactions/TransactionListPage.js'), 'TransactionListPage');
const TransactionLogPage = lazyPage(() => import('./features/transaction-log/TransactionLogPage.js'), 'TransactionLogPage');
const TransactionCreatePage = lazyPage(() => import('./features/transactions/TransactionCreatePage.js'), 'TransactionCreatePage');
const TransactionDetailPage = lazyPage(() => import('./features/transactions/TransactionDetailPage.js'), 'TransactionDetailPage');
const TransactionDeskWorkspacePage = lazyPage(() => import('./features/transactions/TransactionDeskWorkspacePage.js'), 'TransactionDeskWorkspacePage');
const TransactFormsPage = lazyPage(() => import('./features/transactions/TransactFormsPage.js'), 'TransactFormsPage');
const TransactFileDetailPage = lazyPage(() => import('./features/transactions/TransactFileDetailPage.js'), 'TransactFileDetailPage');
const TransactSigningsOverviewPage = lazyPage(() => import('./features/transactions/TransactSigningsOverviewPage.js'), 'TransactSigningsOverviewPage');
const TransactBrowserJobsPage = lazyPage(() => import('./features/transactions/TransactBrowserJobsPage.js'), 'TransactBrowserJobsPage');
const TransactBrowserJobDetailPage = lazyPage(() => import('./features/transactions/TransactBrowserJobsPage.js'), 'TransactBrowserJobDetailPage');
const TransactFormFillGuidePage = lazyPage(() => import('./features/transactions/TransactFormFillGuidePage.js'), 'TransactFormFillGuidePage');
const TransactFormMapEditorPage = lazyPage(() => import('./features/transactions/TransactFormMapEditorPage.js'), 'TransactFormMapEditorPage');
const ChecklistTemplatesPage = lazyPage(() => import('./features/transactions/checklist/ChecklistTemplatesPage.js'), 'ChecklistTemplatesPage');
const CalendarPage = lazyPage(() => import('./features/calendar/CalendarPage.js'), 'CalendarPage');
const TeamsPage = lazyPage(() => import('./features/teams/TeamsPage.js'), 'TeamsPage');
const ShowFlowListPage = lazyPage(() => import('./features/showflow/ShowFlowListPage.js'), 'ShowFlowListPage');
const TourDetailPage = lazyPage(() => import('./features/showflow/TourDetailPage.js'), 'TourDetailPage');
const RunTourPage = lazyPage(() => import('./features/showflow/RunTourPage.js'), 'RunTourPage');
const BuyerTourPage = lazyPage(() => import('./features/showflow/BuyerTourPage.js'), 'BuyerTourPage');
const TourPacketPage = lazyPage(() => import('./features/showflow/TourPacketPage.js'), 'TourPacketPage');
const TourComparePage = lazyPage(() => import('./features/showflow/TourComparePage.js'), 'TourComparePage');
const ListingAgentPropertyPage = lazyPage(() => import('./features/showflow/ListingAgentPropertyPage.js'), 'ListingAgentPropertyPage');
const MlsFavoritesPage = lazyPage(() => import('./features/mls/MlsFavoritesPage.js'), 'MlsFavoritesPage');
const MlsSearchPage = lazyPage(() => import('./features/mls/MlsSearchPage.js'), 'MlsSearchPage');
const MlsListingDetailPage = lazyPage(() => import('./features/mls/MlsListingDetailPage.js'), 'MlsListingDetailPage');
const MailingsListPage = lazyPage(() => import('./features/mailings/MailingsListPage.js'), 'MailingsListPage');
const MailingDetailPage = lazyPage(() => import('./features/mailings/MailingDetailPage.js'), 'MailingDetailPage');
const MailingBlocksPage = lazyPage(() => import('./features/mailings/MailingBlocksPage.js'), 'MailingBlocksPage');
const MailingsMapPage = lazyPage(() => import('./features/mailings/MailingsMapPage.js'), 'MailingsMapPage');
const MarketingListPage = lazyPage(() => import('./features/marketing/MarketingListPage.js'), 'MarketingListPage');
const LandingPagesPage = lazyPage(() => import('./features/marketing/landing-pages/LandingPagesPage.js'), 'LandingPagesPage');
const LandingPageEditorPage = lazyPage(() => import('./features/marketing/landing-pages/LandingPageEditorPage.js'), 'LandingPageEditorPage');
const MarketingCampaignPage = lazyPage(() => import('./features/marketing/MarketingCampaignPage.js'), 'MarketingCampaignPage');
const PropertyListsPage = lazyPage(() => import('./features/marketing/PropertyListsPage.js'), 'PropertyListsPage');
const MarketingStyleEditorPage = lazyPage(() => import('./features/marketing/MarketingStyleEditorPage.js'), 'MarketingStyleEditorPage');
const MarketingTemplateStyleEditorPage = lazyPage(() => import('./features/marketing/MarketingTemplateStyleEditorPage.js'), 'MarketingTemplateStyleEditorPage');
const IcePropertyDetailPage = lazyPage(() => import('./features/mailings/PropertyDetailPage.js'), 'PropertyDetailPage');
const AdminUsersPage = lazyPage(() => import('./features/admin/AdminUsersPage.js'), 'AdminUsersPage');
const AdminPortalAccountsPage = lazyPage(
  () => import('./features/admin/AdminPortalAccountsPage.js'),
  'AdminPortalAccountsPage',
);
const AdminTeamPage = lazyPage(() => import('./features/admin/AdminTeamPage.js'), 'AdminTeamPage');
const AdminSecurityPage = lazyPage(() => import('./features/admin/AdminSecurityPage.js'), 'AdminSecurityPage');
const AdminSystemStatusPage = lazyPage(() => import('./features/admin/AdminSystemStatusPage.js'), 'AdminSystemStatusPage');
const AdminSettingsPage = lazyPage(() => import('./features/admin/AdminSettingsPage.js'), 'AdminSettingsPage');
const AdminAiPromptsPage = lazyPage(() => import('./features/admin/AdminAiPromptsPage.js'), 'AdminAiPromptsPage');
const AdminCrmOptionsPage = lazyPage(() => import('./features/admin/AdminCrmOptionsPage.js'), 'AdminCrmOptionsPage');
const AdminMlsPage = lazyPage(() => import('./features/admin/AdminMlsPage.js'), 'AdminMlsPage');
const AvmAdminPage = lazyPage(() => import('./features/avm-admin/AvmAdminPage.js'), 'AvmAdminPage');
const ValuationsPage = lazyPage(() => import('./features/valuations/ValuationsPage.js'), 'ValuationsPage');
const AdminParcelCoordsPage = lazyPage(
  () => import('./features/admin/AdminParcelCoordsPage.js'),
  'AdminParcelCoordsPage',
);
const AdminLockboxesPage = lazyPage(() => import('./features/admin/AdminLockboxesPage.js'), 'AdminLockboxesPage');
const CmaListPage = lazyPage(() => import('./features/cma/CmaListPage.js'), 'CmaListPage');
const CmaDetailPage = lazyPage(() => import('./features/cma/CmaDetailPage.js'), 'CmaDetailPage');
const CmaReportPage = lazyPage(() => import('./features/cma/CmaReportPage.js'), 'CmaReportPage');
const CmaPublicSharePage = lazyPage(() => import('./features/cma/CmaPublicSharePage.js'), 'CmaPublicSharePage');
const GoodFaithEstimatesPage = lazyPage(() => import('./features/good-faith-estimates/GoodFaithEstimatesPage.js'), 'GoodFaithEstimatesPage');
const GoodFaithEstimateReportPage = lazyPage(() => import('./features/good-faith-estimates/GoodFaithEstimatesPage.js'), 'GoodFaithEstimateReportPage');
const PublicGoodFaithEstimatePage = lazyPage(() => import('./features/good-faith-estimates/GoodFaithEstimatesPage.js'), 'PublicGoodFaithEstimatePage');
const OfferSummariesPage = lazyPage(() => import('./features/offer-summaries/OfferSummariesPage.js'), 'OfferSummariesPage');
const OfferSummaryReportPage = lazyPage(() => import('./features/offer-summaries/OfferSummariesPage.js'), 'OfferSummaryReportPage');
const PublicOfferSummaryPage = lazyPage(() => import('./features/offer-summaries/OfferSummariesPage.js'), 'PublicOfferSummaryPage');
const ActivityReportPublicPage = lazyPage(() => import('./features/tracking/ActivityReportPublicPage.js'), 'ActivityReportPublicPage');
const BuyersListPage = lazyPage(() => import('./features/buyers/BuyersListPage.js'), 'BuyersListPage');
const BuyerDetailPage = lazyPage(() => import('./features/buyers/BuyerDetailPage.js'), 'BuyerDetailPage');
const BuyerInviteRedeemPage = lazyPage(() => import('./features/buyers/BuyerInviteRedeemPage.js'), 'BuyerInviteRedeemPage');
const BuyerDashboardPage = lazyPage(() => import('./features/buyers/BuyerDashboardPage.js'), 'BuyerDashboardPage');
const BuyerToursPage = lazyPage(() => import('./features/buyers/BuyerToursPage.js'), 'BuyerToursPage');
const BuyerTourRequestsPage = lazyPage(() => import('./features/buyers/BuyerTourRequestsPage.js'), 'BuyerTourRequestsPage');
const BuyerCmaPage = lazyPage(() => import('./features/buyers/BuyerCmaPage.js'), 'BuyerCmaPage');
const BuyerSearchPage = lazyPage(() => import('./features/buyers/BuyerSearchPage.js'), 'BuyerSearchPage');
const BuyerListingDetailPage = lazyPage(() => import('./features/buyers/BuyerListingDetailPage.js'), 'BuyerListingDetailPage');
const BuyerFavoritesPage = lazyPage(() => import('./features/buyers/BuyerFavoritesPage.js'), 'BuyerFavoritesPage');
const BuyerSavedSearchesPage = lazyPage(() => import('./features/buyers/BuyerSavedSearchesPage.js'), 'BuyerSavedSearchesPage');
const BuyerDocumentsPage = lazyPage(() => import('./features/buyers/BuyerDocumentsPage.js'), 'BuyerDocumentsPage');
const BuyerLenderPage = lazyPage(() => import('./features/buyers/BuyerLenderPage.js'), 'BuyerLenderPage');
const BuyerEmailPage = lazyPage(() => import('./features/buyers/BuyerEmailPage.js'), 'BuyerEmailPage');
const SharedListingRedirect = lazyPage(() => import('./features/shared-listings/SharedListingRedirect.js'), 'SharedListingRedirect');
const SharedListingsListPage = lazyPage(() => import('./features/shared-listings/SharedListingsListPage.js'), 'SharedListingsListPage');
const TourRequestsPage = lazyPage(() => import('./features/buyers/TourRequestsPage.js'), 'TourRequestsPage');
const TasksPage = lazyPage(() => import('./features/tasks/TasksPage.js'), 'TasksPage');
const SellersListPage = lazyPage(() => import('./features/sellers/SellersListPage.js'), 'SellersListPage');
const ReferralsListPage = lazyPage(() => import('./features/referrals/ReferralsListPage.js'), 'ReferralsListPage');
const ReferralCreatePage = lazyPage(() => import('./features/referrals/ReferralCreatePage.js'), 'ReferralCreatePage');
const ReferralDetailPage = lazyPage(() => import('./features/referrals/ReferralDetailPage.js'), 'ReferralDetailPage');
const ReferralOfferPublicPage = lazyPage(() => import('./features/referrals/ReferralOfferPublicPage.js'), 'ReferralOfferPublicPage');
const SellerDetailPage = lazyPage(() => import('./features/sellers/SellerDetailPage.js'), 'SellerDetailPage');
const SellerInviteRedeemPage = lazyPage(() => import('./features/sellers/SellerInviteRedeemPage.js'), 'SellerInviteRedeemPage');
const SellerDashboardPage = lazyPage(() => import('./features/sellers/SellerDashboardPage.js'), 'SellerDashboardPage');
const SellerPropertyPage = lazyPage(() => import('./features/sellers/SellerPropertyPage.js'), 'SellerPropertyPage');
const SellerPropertyTrackingPage = lazyPage(() => import('./features/sellers/SellerPropertyTrackingPage.js'), 'SellerPropertyTrackingPage');
const VoicemailInboxPage = lazyPage(() => import('./features/voice/VoicemailInboxPage.js'), 'VoicemailInboxPage');
const PublicSavedSearchPage = lazyPage(() => import('./features/mls/PublicSavedSearchPage.js'), 'PublicSavedSearchPage');
const PublicSharedListingsPage = lazyPage(() => import('./features/mls/PublicSharedListingsPage.js'), 'PublicSharedListingsPage');
const PublicSharedListingSetListingPage = lazyPage(() => import('./features/mls/PublicSharedListingSetListingPage.js'), 'PublicSharedListingSetListingPage');

const ME_KEY = ['auth', 'me'] as const;

// If /api/auth/me hangs (e.g. a browser extension intercepting fetch and never
// resolving), don't block the whole app on it — fall back to "unauthenticated"
// after a short timeout so the login route can still render.
const ME_TIMEOUT_MS = 5000;

async function fetchMe(): Promise<User | null> {
  const cached = queryClient.getQueryData<User | null>(ME_KEY);
  if (cached !== undefined) return cached;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ME_TIMEOUT_MS);
  try {
    const { user } = await apiFetch('/api/auth/me', MeResponse, { signal: controller.signal });
    queryClient.setQueryData(ME_KEY, user);
    return user;
  } catch (err) {
    if ((err as { statusCode?: number }).statusCode === 401) {
      queryClient.setQueryData(ME_KEY, null);
      return null;
    }
    if ((err as { name?: string }).name === 'AbortError') {
      // Don't cache — a retry on the next navigation may succeed.
      return null;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function requireUser() {
  const user = await fetchMe();
  if (!user) throw redirect({ to: '/login' });
}

async function requireRole(role: User['role']) {
  const user = await fetchMe();
  if (!user) throw redirect({ to: '/login' });
  if (user.role !== role) throw redirect({ to: '/' });
}

function requireAdmin() {
  return requireRole('admin');
}

function requireBuyer() {
  return requireRole('buyer');
}

function requireSeller() {
  return requireRole('seller');
}

const PositiveIntegerSearchParam = z.preprocess((value) => {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return undefined;
}, z.number().int().positive().optional());

const OptionalTextSearchParam = z.preprocess((value) => {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  return undefined;
}, z.string().optional());

const TeamsViewSearchParam = z.preprocess((value) => {
  // Derive the allowlist from the enum instead of restating it — a
  // hard-coded copy silently swallowed the 'drafts' view when it was
  // added, leaving the tab unreachable by URL.
  return TeamsWorkspaceView.safeParse(value).success ? value : undefined;
}, TeamsWorkspaceView.optional());

const MailRouteSearch = z.object({
  messageId: PositiveIntegerSearchParam,
  folder: OptionalTextSearchParam,
});

const CommsRouteSearch = z.object({
  contactId: PositiveIntegerSearchParam,
});

const TeamsRouteSearch = z.object({
  view: TeamsViewSearchParam,
  chatId: OptionalTextSearchParam,
  teamId: OptionalTextSearchParam,
  channelId: OptionalTextSearchParam,
});

const VoicemailRouteSearch = z.object({
  voicemailId: PositiveIntegerSearchParam,
});

const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  ),
});

// Layout route — mounts the app chrome (sidebar / topbar / backdrop /
// main) exactly once. Every authed route is parented to this so the
// DOM persists across navigations; pages only publish title + actions
// into the shared shell-frame store via `<Shell title="X">`.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: () => (
    <AppLayout>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </AppLayout>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: async () => {
    const user = await fetchMe();
    if (user) throw redirect({ to: '/' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: DashboardPage,
  beforeLoad: requireUser,
});

const contactListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm',
  component: ContactListPage,
  beforeLoad: requireUser,
});

const veteransUnitedRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/veterans-united',
  component: VeteransUnitedPage,
  beforeLoad: requireAdmin,
});

const vuLeadDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/veterans-united/$leadId',
  component: VuLeadDetailPage,
  beforeLoad: requireAdmin,
});

const contactDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/contacts/$id',
  component: ContactDetailPage,
  validateSearch: contactActivitySearch,
  beforeLoad: requireUser,
});

const websiteActivityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/website-activity',
  component: WebsiteActivityPage,
  validateSearch: activitySearch,
  beforeLoad: async () => {
    const user = await fetchMe();
    if (!user) throw redirect({ to: '/login' });
    if (!isAgentLikeRole(user.role)) throw redirect({ to: '/' });
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: SettingsPage,
  validateSearch: (
    search,
  ): { ms: string | undefined; reason: string | undefined; section?: 'voicemail' | undefined } => ({
    ms: typeof search.ms === 'string' ? search.ms : undefined,
    reason: typeof search.reason === 'string' ? search.reason : undefined,
    // Deep link to one card (the Voicemail page's "Greeting" button).
    // Optional so the existing `search={{ ms, reason }}` links stay valid.
    ...(search.section === 'voicemail' ? { section: 'voicemail' as const } : {}),
  }),
  beforeLoad: requireUser,
});

const reviewsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/reviews',
  component: ReviewsPage,
  beforeLoad: requireUser,
});

// Public route — no auth check. The token in the URL is the sole
// authorization for viewing and submitting the review.
const publicReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review/$token',
  component: PublicReviewPage,
});

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPolicyPage,
});

const termsOfServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsOfServicePage,
});

// Dev-only design preview for the Side Drawer Cockpit dialer. Standalone
// page (no AppLayout, no auth) so it renders with or without the Twilio
// voice creds being configured. Safe to leave in prod — it just shows a
// static demo of three mocked-up call states.
const cockpitPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dialer-preview',
  component: CockpitPreviewPage,
});

// Dev-only preview for the Text Messaging Cockpit. Same shell + tokens
// as the dialer cockpit; flips between Inbox / Conversation / Compose /
// Bulk Blast surfaces with synthetic data so the design can be
// reviewed before the production data wiring lands.
const messagingPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/messaging-preview',
  component: MessagingPreviewPage,
});

const dedupeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/crm/dedupe',
  component: DedupePage,
  beforeLoad: requireUser,
});

const mailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mail',
  component: MailPage,
  validateSearch: (search: Record<string, unknown>) => MailRouteSearch.parse(search),
  beforeLoad: requireUser,
});

const filesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/files',
  component: FilesPage,
  beforeLoad: requireUser,
});

const commsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/comms',
  component: CommsPage,
  validateSearch: (search: Record<string, unknown>) => CommsRouteSearch.parse(search),
  beforeLoad: requireUser,
});

const commsAnalyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/comms/analytics',
  component: CommsAnalyticsPage,
  beforeLoad: requireUser,
});

// Group texting lives on its own route rather than inside the Phone & Text
// inbox — telling group threads apart from 1:1 threads should be a matter of
// which page you are on, not of noticing a badge.
const commsGroupsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/comms/groups',
  component: GroupsPage,
  beforeLoad: requireUser,
});

// Facebook Messenger gets its own page for the same reason group texts do
// (Rule 13): the channel a reply goes out on is decided by which page you
// are on, never by a row badge inside another inbox.
const commsMessengerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/comms/messenger',
  component: MessengerPage,
  beforeLoad: requireUser,
});

const transactionListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions',
  component: TransactionListPage,
  beforeLoad: requireUser,
});

const transactionLogRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transaction-log',
  component: TransactionLogPage,
  beforeLoad: requireUser,
});

const transactionCreateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/new',
  component: TransactionCreatePage,
  beforeLoad: requireUser,
});

const transactionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/$id',
  component: TransactionDetailPage,
  beforeLoad: requireUser,
});

const transactionDeskWorkspaceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/td/$id',
  component: TransactionDeskWorkspacePage,
  beforeLoad: requireUser,
});

const transactFormsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/forms',
  component: TransactFormsPage,
  beforeLoad: requireUser,
});

// The broker's form set-up (ADR 0021): one per Lone Wolf form version, opened
// from a file (`?from=`) whose published render of the form it draws on. Map
// writes are admin-only on the API too; this gate keeps the page out of reach.
const TransactFormMapSearch = z.object({ from: OptionalTextSearchParam });

const transactFormMapRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/forms/$versionId/map',
  component: TransactFormMapEditorPage,
  validateSearch: (search: Record<string, unknown>) => TransactFormMapSearch.parse(search),
  beforeLoad: requireAdmin,
});

const transactFileDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/transact/$transactionId',
  component: TransactFileDetailPage,
  beforeLoad: requireUser,
});

const transactSigningsOverviewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/signings',
  component: TransactSigningsOverviewPage,
  beforeLoad: requireUser,
});

// The Transact browser worker's jobs (ADR 0022 decision 11): every route is
// admin only on the API too; this gate keeps the pages out of reach.
const transactBrowserJobsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/browser-jobs',
  component: TransactBrowserJobsPage,
  beforeLoad: requireAdmin,
});

const transactBrowserJobDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/browser-jobs/$jobId',
  component: TransactBrowserJobDetailPage,
  beforeLoad: requireAdmin,
});

// "Fill in Lone Wolf" (ADR 0021): a small window opened beside Lone Wolf's
// own form editor, listing the values YRP keeps for one attached form. No app
// chrome, so it fits a narrow window. The API decides who may read the file.
const transactFormFillGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transactions/$transactionId/forms/$attachedFormId/guide',
  component: TransactFormFillGuidePage,
  beforeLoad: requireUser,
});

// Keep old bookmarks working under the same admin guard as the canonical page.
const checklistTemplatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/transactions/checklist-templates',
  component: ChecklistTemplatesPage,
  beforeLoad: requireAdmin,
});

const adminChecklistsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/checklists',
  component: ChecklistTemplatesPage,
  beforeLoad: requireAdmin,
});

const calendarRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/calendar',
  component: CalendarPage,
  beforeLoad: requireUser,
});

const teamsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/teams',
  component: TeamsPage,
  validateSearch: (search: Record<string, unknown>) => TeamsRouteSearch.parse(search),
  beforeLoad: requireUser,
});

const showflowListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/showflow',
  component: ShowFlowListPage,
  beforeLoad: requireUser,
});

const mailingsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mailings',
  component: MailingsListPage,
  beforeLoad: requireAdmin,
});

const mailingDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mailings/$id',
  component: MailingDetailPage,
  beforeLoad: requireAdmin,
});

const mailingBlocksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mailings/blocks',
  component: MailingBlocksPage,
  beforeLoad: requireAdmin,
});

const mailingsMapRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mailings/map',
  component: MailingsMapPage,
  beforeLoad: requireAdmin,
});

const marketingListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing',
  component: MarketingListPage,
  beforeLoad: requireUser,
});

// Registered ahead of the '/marketing/$id' campaign route below. The
// static segment has to win over the dynamic one, or "property-lists"
// is read as a campaign id.
const propertyListsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing/property-lists',
  component: PropertyListsPage,
  beforeLoad: requireUser,
});

const marketingCampaignRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing/$id',
  component: MarketingCampaignPage,
  beforeLoad: requireUser,
});

const landingPagesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing/landing-pages',
  component: LandingPagesPage,
  beforeLoad: requireUser,
});

const landingPageEditorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing/landing-pages/$pageId',
  component: LandingPageEditorPage,
  beforeLoad: requireUser,
});

const marketingStyleEditorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing/$id/style',
  component: MarketingStyleEditorPage,
  beforeLoad: requireUser,
});

const marketingTemplateStyleEditorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketing/templates/$id/style',
  component: MarketingTemplateStyleEditorPage,
  beforeLoad: requireUser,
});

const iceParcelDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/property/$apn',
  component: IcePropertyDetailPage,
  beforeLoad: requireAdmin,
});

const tourDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/showflow/tours/$id',
  component: TourDetailPage,
  beforeLoad: requireUser,
});

const runTourRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/showflow/tours/$id/run',
  component: RunTourPage,
  beforeLoad: requireUser,
});

const tourPacketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/showflow/tours/$id/packet',
  component: TourPacketPage,
  validateSearch: (search) => ({
    print: search.print === '1' ? '1' : undefined,
  }),
  beforeLoad: requireUser,
});

const tourCompareRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/showflow/tours/$id/compare',
  component: TourComparePage,
  beforeLoad: requireUser,
});

// Public listing-agent-facing page — no auth, token-gated.
const listingAgentPropertyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/showflow/property/$token',
  component: ListingAgentPropertyPage,
});

// Public route — no auth. Token in URL is the sole authorization.
// Same pattern as /review/$token.
const buyerTourRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/tour/$token',
  component: BuyerTourPage,
});

const mlsSearchRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mls',
  component: MlsSearchPage,
  beforeLoad: requireUser,
});

const mlsFavoritesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mls/favorites',
  component: MlsFavoritesPage,
  beforeLoad: requireUser,
});

const mlsListingDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mls/$market/$key',
  component: MlsListingDetailPage,
  validateSearch: (search: Record<string, unknown>) => {
    const cmaReturn =
      typeof search['cmaReturn'] === 'string' && /^\d+$/.test(search['cmaReturn'])
        ? search['cmaReturn']
        : undefined;
    return cmaReturn !== undefined ? { cmaReturn } : {};
  },
  beforeLoad: requireUser,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/users',
  component: AdminUsersPage,
  beforeLoad: requireAdmin,
});

// Rule 12 pattern: requireAdmin on the client route AND on the API route.
// This one hands out client sessions, so the gate is not optional.
const adminPortalAccountsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/client-portals',
  component: AdminPortalAccountsPage,
  beforeLoad: requireAdmin,
});

const adminTeamRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/team',
  component: AdminTeamPage,
  beforeLoad: requireAdmin,
});

const adminSecurityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/security',
  component: AdminSecurityPage,
  beforeLoad: requireAdmin,
});

const adminMlsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/mls',
  component: AdminMlsPage,
  beforeLoad: requireAdmin,
});

const avmAdminRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/avm',
  component: AvmAdminPage,
  beforeLoad: requireAdmin,
});

const valuationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/valuations',
  component: ValuationsPage,
});

const adminParcelCoordsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/parcel-coords',
  component: AdminParcelCoordsPage,
  beforeLoad: requireAdmin,
});

const adminLockboxesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/lockboxes',
  component: AdminLockboxesPage,
  beforeLoad: requireAdmin,
});

const adminSystemStatusRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/status',
  component: AdminSystemStatusPage,
  beforeLoad: requireAdmin,
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/settings',
  component: AdminSettingsPage,
  beforeLoad: requireAdmin,
});

const adminAiPromptsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/ai-prompts',
  component: AdminAiPromptsPage,
  beforeLoad: requireAdmin,
});

const adminCrmOptionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/crm-options',
  component: AdminCrmOptionsPage,
  beforeLoad: requireAdmin,
});

const cmaListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/cma',
  component: CmaListPage,
  beforeLoad: requireUser,
});

const cmaDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/cma/$id',
  component: CmaDetailPage,
  validateSearch: (search: Record<string, unknown>) =>
    search['comps'] === '1' ? { comps: '1' as const } : {},
  beforeLoad: requireUser,
});

const goodFaithEstimatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/good-faith-estimates',
  component: GoodFaithEstimatesPage,
  beforeLoad: requireUser,
});

const goodFaithEstimateReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/good-faith-estimates/$id/report',
  component: GoodFaithEstimateReportPage,
  beforeLoad: requireUser,
});

const goodFaithEstimatePublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gfe/$token',
  component: PublicGoodFaithEstimatePage,
});

const goodFaithEstimateShortPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/e/$token',
  component: PublicGoodFaithEstimatePage,
});

const offerSummariesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/offer-summaries',
  component: OfferSummariesPage,
  beforeLoad: requireUser,
});

const offerSummaryReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offer-summaries/$id/report',
  component: OfferSummaryReportPage,
  beforeLoad: requireUser,
});

const offerSummaryPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/os/$token',
  component: PublicOfferSummaryPage,
});

const cmaReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cma/$id/report',
  component: CmaReportPage,
  validateSearch: (search: Record<string, unknown>) => ({
    print: search['print'] === '1' ? '1' : undefined,
    pdf: search['pdf'] === '1' ? '1' : undefined,
  }),
  beforeLoad: requireUser,
});

const cmaReportPdfRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cma/$id/report/pdf',
  component: CmaReportPage,
  beforeLoad: requireUser,
});

const cmaPublicShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cma/share/$token',
  component: CmaPublicSharePage,
  // Intentionally no beforeLoad — token in the URL is the sole
  // authorization, same as the review + buyer-tour share flows.
});

const cmaShortShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/$token',
  component: CmaPublicSharePage,
  // Short public CMA URL. Token/slug-only authz, same backing API.
});

const activityReportPublicShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activity-report/share/$token',
  component: ActivityReportPublicPage,
  // Token-only authz, no beforeLoad.
});

const buyersListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/buyers',
  component: BuyersListPage,
  beforeLoad: requireUser,
});

const buyerDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/buyers/$id',
  component: BuyerDetailPage,
  beforeLoad: requireUser,
});

const buyerInviteRedeemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/invite/$token',
  component: BuyerInviteRedeemPage,
  // No beforeLoad — token is sole authorization.
});

const buyerDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer',
  component: BuyerDashboardPage,
  beforeLoad: requireBuyer,
});

const buyerToursRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/tours',
  component: BuyerToursPage,
  beforeLoad: requireBuyer,
});

const buyerTourRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/tour-requests',
  component: BuyerTourRequestsPage,
  beforeLoad: requireBuyer,
});

const buyerCmaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/cma',
  component: BuyerCmaPage,
  beforeLoad: requireBuyer,
});

const buyerSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/search',
  component: BuyerSearchPage,
  beforeLoad: requireBuyer,
});

const buyerListingDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/listing/$market/$key',
  component: BuyerListingDetailPage,
  beforeLoad: requireBuyer,
});

const buyerFavoritesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/favorites',
  component: BuyerFavoritesPage,
  beforeLoad: requireBuyer,
});

const buyerSavedSearchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/saved-searches',
  component: BuyerSavedSearchesPage,
  beforeLoad: requireBuyer,
});

const buyerDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/documents',
  component: BuyerDocumentsPage,
  beforeLoad: requireBuyer,
});

const buyerLenderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/lender',
  component: BuyerLenderPage,
  beforeLoad: requireBuyer,
});

const buyerEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer/email',
  component: BuyerEmailPage,
  beforeLoad: requireBuyer,
});

// Public — no auth. Old links to the dashboard's own shared-listing page:
// they now forward through /sl/:token to the public site's shared-listing
// page, which every dashboard share opens (SharedListingRedirect).
const sharedListingPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shared/$token',
  component: SharedListingRedirect,
});

const sharedListingShortPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$token',
  component: SharedListingRedirect,
});

const sharedListingsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/shared-listings',
  component: SharedListingsListPage,
  beforeLoad: requireUser,
});

const agentTourRequestsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/buyers/tour-requests',
  component: TourRequestsPage,
  beforeLoad: requireUser,
});

const tasksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tasks',
  component: TasksPage,
  beforeLoad: requireUser,
});

const sellersListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sellers',
  component: SellersListPage,
  beforeLoad: requireUser,
});

const referralsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/referrals',
  component: ReferralsListPage,
  beforeLoad: requireUser,
});

const referralCreateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/referrals/new',
  component: ReferralCreatePage,
  beforeLoad: requireUser,
});

const referralDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/referrals/$id',
  component: ReferralDetailPage,
  beforeLoad: requireUser,
});

// Public outside-agent page — offer review, accept/decline, e-sign,
// and post-signature portal. Token in the URL is the sole
// authorization, same pattern as /review/$token.
const referralOfferPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/referral/$token',
  component: ReferralOfferPublicPage,
});

const sellerDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sellers/$id',
  component: SellerDetailPage,
  beforeLoad: requireUser,
});

const sellerPropertyTrackingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sellers/$id/properties/$propertyId/tracking',
  component: SellerPropertyTrackingPage,
  beforeLoad: requireUser,
});

const sellerInviteRedeemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/seller/invite/$token',
  component: SellerInviteRedeemPage,
  // Intentionally NO beforeLoad — the magic-link token is the sole
  // authorization; any logged-in session gets swapped for the
  // seller's on successful redemption.
});

const sellerDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/seller',
  component: SellerDashboardPage,
  beforeLoad: requireSeller,
});

const sellerPropertyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/seller/properties/$id',
  component: SellerPropertyPage,
  beforeLoad: requireSeller,
});

const voicemailInboxRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/voicemail',
  component: VoicemailInboxPage,
  validateSearch: (search: Record<string, unknown>) => VoicemailRouteSearch.parse(search),
  beforeLoad: requireUser,
});

// Public — no auth. Token in URL is the sole authorization, same
// pattern as /review/$token, /cma/share/$token, /buyer/tour/$token.
const publicSavedSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search/share/$token',
  component: PublicSavedSearchPage,
});

// Public — hand-picked set of MLS listings shared as one short `/s/:token`
// link from MLS Search. Distinct from the single-listing share at `/l/:token`.
const publicSharedListingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$token',
  component: PublicSharedListingsPage,
});

// Public — detail view for one listing inside a shared set. Clicking a card
// on `/s/:token` lands here; the collection dock navigates between the
// set's listings without leaving the share.
const publicSharedListingSetListingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$token/$listingKey',
  component: PublicSharedListingSetListingPage,
});

const appLayoutWithChildren = appLayoutRoute.addChildren([
  dashboardRoute,
  contactListRoute,
  veteransUnitedRoute,
  vuLeadDetailRoute,
  contactDetailRoute,
  websiteActivityRoute,
  settingsRoute,
  reviewsRoute,
  dedupeRoute,
  mailRoute,
  filesRoute,
  commsRoute,
  commsAnalyticsRoute,
  commsGroupsRoute,
  commsMessengerRoute,
  transactionListRoute,
  transactionCreateRoute,
  transactFormsRoute,
  transactFormMapRoute,
  transactFileDetailRoute,
  transactSigningsOverviewRoute,
  transactBrowserJobsRoute,
  transactBrowserJobDetailRoute,
  checklistTemplatesRoute,
  adminChecklistsRoute,
  transactionDeskWorkspaceRoute,
  transactionDetailRoute,
  transactionLogRoute,
  calendarRoute,
  teamsRoute,
  showflowListRoute,
  tourDetailRoute,
  tourCompareRoute,
  mailingsListRoute,
  mailingDetailRoute,
  mailingBlocksRoute,
  mailingsMapRoute,
  marketingListRoute,
  landingPagesRoute,
  landingPageEditorRoute,
  propertyListsRoute,
  marketingCampaignRoute,
  marketingStyleEditorRoute,
  marketingTemplateStyleEditorRoute,
  iceParcelDetailRoute,
  mlsSearchRoute,
  mlsFavoritesRoute,
  mlsListingDetailRoute,
  adminUsersRoute,
  adminPortalAccountsRoute,
  adminTeamRoute,
  adminSecurityRoute,
  adminMlsRoute,
  avmAdminRoute,
  valuationsRoute,
  adminParcelCoordsRoute,
  adminLockboxesRoute,
  adminSystemStatusRoute,
  adminSettingsRoute,
  adminAiPromptsRoute,
  adminCrmOptionsRoute,
  cmaListRoute,
  cmaDetailRoute,
  goodFaithEstimatesRoute,
  offerSummariesRoute,
  buyersListRoute,
  buyerDetailRoute,
  sharedListingsListRoute,
  agentTourRequestsRoute,
  tasksRoute,
  sellersListRoute,
  sellerDetailRoute,
  sellerPropertyTrackingRoute,
  referralsListRoute,
  referralCreateRoute,
  referralDetailRoute,
  voicemailInboxRoute,
]);

const routeTree = rootRoute.addChildren([
  loginRoute,
  appLayoutWithChildren,
  publicReviewRoute,
  privacyPolicyRoute,
  termsOfServiceRoute,
  cockpitPreviewRoute,
  messagingPreviewRoute,
  runTourRoute,
  tourPacketRoute,
  listingAgentPropertyRoute,
  buyerTourRoute,
  cmaReportRoute,
  cmaReportPdfRoute,
  transactFormFillGuideRoute,
  cmaPublicShareRoute,
  cmaShortShareRoute,
  goodFaithEstimateReportRoute,
  goodFaithEstimateShortPublicRoute,
  goodFaithEstimatePublicRoute,
  offerSummaryReportRoute,
  offerSummaryPublicRoute,
  activityReportPublicShareRoute,
  publicSavedSearchRoute,
  publicSharedListingsRoute,
  publicSharedListingSetListingRoute,
  sharedListingShortPublicRoute,
  buyerInviteRedeemRoute,
  buyerDashboardRoute,
  buyerToursRoute,
  buyerTourRequestsRoute,
  buyerCmaRoute,
  buyerSearchRoute,
  buyerListingDetailRoute,
  buyerFavoritesRoute,
  buyerSavedSearchesRoute,
  buyerDocumentsRoute,
  buyerLenderRoute,
  buyerEmailRoute,
  sharedListingPublicRoute,
  sellerInviteRedeemRoute,
  sellerDashboardRoute,
  sellerPropertyRoute,
  referralOfferPublicRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Render this when a route's component throws OR a lazy chunk fetch
  // fails. Without it, an unhandled error renders a totally blank page
  // — Kenneth reported intermittent white screens on nav-bar clicks
  // (typically transient chunk-fetch failures behind DO Spaces). The
  // fallback gives the user a clear "try again" path instead of
  // staring at white.
  defaultErrorComponent: RouteError,
});

const CHUNK_RELOAD_PREFIX = 'yrp:chunk-reload:';

function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk') ||
    message.includes('chunkloaderror')
  );
}

function RouteError({ error, reset }: { error: Error; reset: () => void }) {
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!isChunkError) return;

    const reloadKey = `${CHUNK_RELOAD_PREFIX}${window.location.pathname}`;
    const alreadyReloaded = sessionStorage.getItem(reloadKey);
    if (alreadyReloaded === error.message) return;

    sessionStorage.setItem(reloadKey, error.message);
    window.location.reload();
  }, [error.message, isChunkError]);

  return (
    <div role="alert" className="route-error-shell">
      <div className="card card-body route-error-card">
        <h2 className="route-error-title">Something went wrong</h2>
        <p className="text-muted route-error-text">
          {isChunkError
            ? 'A new version was deployed while this page was open. Refresh the page to load the latest app files.'
            : error.message || 'Unexpected error.'}
        </p>
        <div className="route-error-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (isChunkError) {
                window.location.reload();
                return;
              }
              reset();
            }}
          >
            {isChunkError ? 'Refresh page' : 'Try again'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
